const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

export const CHAT_ACTIVE_NOW_WINDOW_MS = 2 * MINUTE_MS;
export const CHAT_RECENT_ACTIVE_WINDOW_MS = 12 * HOUR_MS;

export type ChatPresenceState = {
  lastActiveAt: Date | null;
  isActiveNow: boolean;
  label: string | null;
  kind: "active" | "recent" | "offline";
};

const coerceDate = (value: unknown) => {
  if (value instanceof Date) return value;
  if (typeof value === "string" || typeof value === "number") {
    const next = new Date(value);
    return Number.isNaN(next.getTime()) ? null : next;
  }
  return null;
};

const formatRelativeLastActive = (date: Date) => {
  const diffMs = Math.max(0, Date.now() - date.getTime());

  if (diffMs < HOUR_MS) {
    const minutes = Math.max(1, Math.round(diffMs / MINUTE_MS));
    return `${minutes}m ago`;
  }

  const hours = Math.max(1, Math.round(diffMs / HOUR_MS));
  return `${hours}h ago`;
};

export function resolveChatPresenceState(
  value: Date | string | number | null | undefined,
): ChatPresenceState {
  const lastActiveAt = coerceDate(value);

  if (!lastActiveAt) {
    return {
      lastActiveAt: null,
      isActiveNow: false,
      label: null,
      kind: "offline",
    };
  }

  const diffMs = Date.now() - lastActiveAt.getTime();
  if (diffMs <= CHAT_ACTIVE_NOW_WINDOW_MS) {
    return {
      lastActiveAt,
      isActiveNow: true,
      label: "Active now",
      kind: "active",
    };
  }

  if (diffMs <= CHAT_RECENT_ACTIVE_WINDOW_MS) {
    return {
      lastActiveAt,
      isActiveNow: false,
      label: `Active ${formatRelativeLastActive(lastActiveAt)}`,
      kind: "recent",
    };
  }

  return {
    lastActiveAt,
    isActiveNow: false,
    label: null,
    kind: "offline",
  };
}

/**
 * Presence as a particular viewer may see it. "Active now" is never private
 * (WhatsApp's "online"); anything older is last seen, which both sides must
 * share before either sees it. Hidden, the timestamp itself is dropped too —
 * the label is not the only thing a client could read it from.
 */
export function resolveChatPresenceForViewer(
  value: Date | string | number | null | undefined,
  { lastSeenVisible }: { lastSeenVisible: boolean },
): ChatPresenceState {
  const state = resolveChatPresenceState(value);
  if (lastSeenVisible) return state;
  if (state.isActiveNow) return { ...state, lastActiveAt: null };
  return resolveChatPresenceState(null);
}
